import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getEventAnalytics } from "../api/client";
import { useColors } from "../context/ThemeContext";
import { formatNairaFromKobo } from "../utils/money";

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const SERIES = [
  { key: "views", label: "Views", color: "#8fd18a" },
  { key: "checkouts", label: "Checkouts", color: "#7eb6ff" },
  { key: "orders", label: "Paid orders", color: "#f0c674" },
  { key: "revenueKobo", label: "Revenue", color: "#e39bff" },
];

const CARD_HELP = {
  Views: "Number of times people opened your event page.",
  "Unique visitors": "Estimated number of different people who viewed your event.",
  "Tickets sold": "Number of tickets successfully sold.",
  Revenue: "Ticket sales after applicable refunds.",
  "Conversion rate": "Percentage of visitors who completed a purchase.",
  "Check-ins": "Number of tickets used at the event.",
  "Tickets issued": "Tickets sold in this period that are still valid or already used.",
  "Checked in": "Tickets from this period that were scanned at the event.",
  Unused: "Tickets from this period that have not been scanned.",
  Attendance: "Checked-in tickets divided by tickets sold in this period.",
  "Unused now": "Tickets that have been sold and not scanned yet. This ignores the date filter.",
};

const displayCount = (metric) => {
  if (!metric || metric.state === "not_tracked") return "Not tracked";
  if (metric.value == null) return "—";
  return Number(metric.value).toLocaleString("en-NG");
};

const displayMoney = (metric) => {
  if (!metric || metric.state === "not_tracked") return "Not tracked";
  return formatNairaFromKobo(metric.value);
};

const displayPercent = (value, state) => {
  if (state === "not_tracked") return "Not tracked";
  if (value == null) return "—";
  return `${value.toFixed(1)}%`;
};

function InfoTip({ label, text }) {
  const C = useColors();
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);

  const place = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(240, window.innerWidth - 24);
    let left = rect.right - width;
    if (left < 12) left = 12;
    if (left + width > window.innerWidth - 12) left = window.innerWidth - width - 12;
    const height = 96;
    let top = rect.bottom + 8;
    if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 8);
    setBox({ left, top, width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false);
    const onPointer = (event) => {
      const target = event.target;
      if (btnRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          border: `1px solid ${C.border}`,
          background: open ? `${C.lime}22` : "transparent",
          color: C.text,
          fontSize: 12,
          fontWeight: 800,
          fontStyle: "italic",
          fontFamily: "Georgia, serif",
          lineHeight: "18px",
          padding: 0,
          cursor: "pointer",
          flex: "0 0 auto",
        }}
      >
        i
      </button>
      {open && box ? createPortal(
        <div
          ref={popRef}
          role="tooltip"
          style={{
            position: "fixed",
            zIndex: 80,
            left: box.left,
            top: box.top,
            width: box.width,
            maxWidth: "calc(100vw - 24px)",
            padding: "10px 12px",
            borderRadius: 12,
            background: C.card,
            color: C.text,
            border: `1px solid ${C.border}`,
            boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {text}
        </div>,
        document.body
      ) : null}
    </>
  );
}

function MetricCard({ label, value, help, note }) {
  const C = useColors();
  return (
    <div style={{
      padding: "12px 12px 14px",
      borderRadius: 14,
      border: `1px solid ${C.border}`,
      background: C.card,
      minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ color: C.muted, fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{label}</div>
        {help ? <InfoTip label={label} text={help} /> : null}
      </div>
      <div style={{
        fontSize: "clamp(18px, 5vw, 26px)",
        fontWeight: 900,
        marginTop: 8,
        letterSpacing: "-0.03em",
        lineHeight: 1.15,
        overflowWrap: "anywhere",
      }}
      >
        {value}
      </div>
      {note ? <div style={{ color: C.lime, fontSize: 12, marginTop: 6 }}>{note}</div> : null}
    </div>
  );
}

function StockLine({ label, value }) {
  const C = useColors();
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: C.muted, fontSize: 11, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function TrendChart({ series, enabled, onToggle, selected, onSelect }) {
  const C = useColors();
  const days = series?.days || [];
  const max = Math.max(
    1,
    ...SERIES.filter((item) => enabled[item.key] && item.key !== "revenueKobo").flatMap((item) => series?.[item.key] || [0])
  );
  const maxRevenue = Math.max(1, ...(enabled.revenueKobo ? series?.revenueKobo || [0] : [0]));
  return (
    <div style={{ padding: 14, borderRadius: 14, border: `1px solid ${C.border}`, background: C.card, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <strong style={{ fontSize: 16 }}>Daily performance</strong>
        <span style={{ color: C.muted, fontSize: 12 }}>Africa/Lagos</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "12px 0" }}>
        {SERIES.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onToggle(item.key)}
            style={{
              border: `1px solid ${enabled[item.key] ? item.color : C.border}`,
              background: enabled[item.key] ? `${item.color}22` : "transparent",
              color: C.text,
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {series?.truncationNote ? (
        <p style={{ color: C.muted, fontSize: 13, marginTop: 0 }}>{series.truncationNote}</p>
      ) : null}
      {days.length === 0 ? (
        <p style={{ color: C.muted, fontSize: 14, marginBottom: 0 }}>No daily activity in this period.</p>
      ) : (
        <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
          <div style={{ display: "flex", gap: 4, alignItems: "flex-end", minHeight: 132, width: "max-content", minWidth: "100%" }}>
            {days.map((day, index) => (
              <button
                key={day}
                type="button"
                onClick={() => onSelect(selected === index ? null : index)}
                style={{
                  border: "none",
                  background: selected === index ? `${C.lime}18` : "transparent",
                  borderRadius: 8,
                  padding: 4,
                  minWidth: 28,
                  flex: "1 0 28px",
                  cursor: "pointer",
                  color: C.text,
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 2, height: 92 }}>
                  {SERIES.filter((item) => enabled[item.key]).map((item) => {
                    const raw = series[item.key]?.[index] || 0;
                    const scale = item.key === "revenueKobo" ? maxRevenue : max;
                    const height = Math.max(2, Math.round((raw / scale) * 92));
                    return (
                      <span
                        key={item.key}
                        style={{
                          display: "block",
                          width: 6,
                          height,
                          borderRadius: 4,
                          background: item.color,
                        }}
                      />
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{day.slice(5)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      {selected != null && days[selected] ? (
        <p style={{ color: C.text, fontSize: 13, marginBottom: 0, lineHeight: 1.4 }}>
          {days[selected]}: {series.views?.[selected] || 0} views, {series.checkouts?.[selected] || 0} checkouts,{" "}
          {series.orders?.[selected] || 0} paid, {formatNairaFromKobo(series.revenueKobo?.[selected] || 0)}.
        </p>
      ) : null}
    </div>
  );
}

function Disclosure({ title, children }) {
  const C = useColors();
  const [open, setOpen] = useState(false);
  return (
    <section style={{ marginTop: 16 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          background: "transparent",
          color: C.text,
          border: "none",
          padding: "4px 0",
          fontWeight: 800,
          fontSize: 18,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span>{title}</span>
        <span aria-hidden="true" style={{ color: C.muted }}>{open ? "−" : "+"}</span>
      </button>
      {open ? children : null}
    </section>
  );
}

export default function EventAnalyticsPanel({ eventId, onBack }) {
  const C = useColors();
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState({ views: true, checkouts: true, orders: true, revenueKobo: false });
  const [selected, setSelected] = useState(null);
  const [moneyOpen, setMoneyOpen] = useState(false);

  useEffect(() => {
    if (range === "custom" && (!from || !to)) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    getEventAnalytics(eventId, { range, from: range === "custom" ? from : undefined, to: range === "custom" ? to : undefined })
      .then((next) => {
        if (!cancelled) {
          setData(next);
          setSelected(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load analytics");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, range, from, to]);

  const summary = data?.summary;
  const pricing = data?.event?.pricing || summary?.pricing || "paid";
  const cards = useMemo(() => {
    if (!summary) return [];
    const views = { label: "Views", value: displayCount(summary.views), help: CARD_HELP.Views, note: summary.views.state === "partial" ? "Partial" : "" };
    const visitors = { label: "Unique visitors", value: displayCount(summary.uniqueVisitors), help: CARD_HELP["Unique visitors"], note: summary.uniqueVisitors.state === "not_tracked" ? "" : "Estimate" };
    const checkIns = { label: "Check-ins", value: displayCount(summary.checkedIn), help: CARD_HELP["Check-ins"] };
    if (pricing === "free") {
      return [
        views,
        visitors,
        { label: "Free registrations", value: displayCount(summary.freeRegistrations), help: "Completed free registrations. These are not paid sales." },
        { label: "Tickets issued", value: displayCount(summary.freeTicketsIssued), help: "Tickets issued from free registrations." },
        { label: "Revenue", value: displayMoney(summary.netRevenue), help: "Ticket revenue. Free registrations add ₦0." },
        { label: "Registration conversion", value: displayPercent(summary.conversionRate?.value, summary.conversionRate?.state), help: "Free registrations divided by estimated unique visitors." },
        checkIns,
      ];
    }
    if (pricing === "mixed") {
      return [
        views,
        visitors,
        { label: "Tickets sold", value: displayCount(summary.ticketsSold), help: CARD_HELP["Tickets sold"] },
        { label: "Free registrations", value: displayCount(summary.freeRegistrations), help: "Completed free registrations. These are not included in tickets sold or revenue." },
        { label: "Revenue", value: displayMoney(summary.netRevenue), help: CARD_HELP.Revenue },
        { label: "Conversion rate", value: displayPercent(summary.conversionRate?.value, summary.conversionRate?.state), help: "Paid orders divided by visitors. Free registrations are not included." },
        checkIns,
      ];
    }
    return [
      views,
      visitors,
      { label: "Tickets sold", value: displayCount(summary.ticketsSold), help: CARD_HELP["Tickets sold"] },
      { label: "Revenue", value: displayMoney(summary.netRevenue), help: CARD_HELP.Revenue },
      { label: "Conversion rate", value: displayPercent(summary.conversionRate.value, summary.conversionRate.state), help: CARD_HELP["Conversion rate"] },
      checkIns,
    ];
  }, [summary, pricing]);

  const reconciliation = summary?.reconciliation;
  const chip = (active) => ({
    border: `1px solid ${active ? C.lime : C.border}`,
    background: active ? `${C.lime}22` : "transparent",
    color: C.text,
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  });

  return (
    <div className="pp-analytics-page">
      <style>{`
        .pp-analytics-page { max-width: 100%; overflow-x: hidden; }
        .pp-analytics-grid, .pp-checkin-grid, .pp-stock-grid {
          display: grid;
          gap: 8px;
        }
        .pp-analytics-grid, .pp-checkin-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .pp-stock-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        @media (max-width: 359px) {
          .pp-stock-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (min-width: 840px) {
          .pp-analytics-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
      `}</style>
      <button type="button" onClick={onBack} style={{
        border: `1px solid ${C.border}`, background: "transparent", color: C.text,
        borderRadius: 10, padding: "8px 12px", marginBottom: 16, cursor: "pointer", fontSize: 14,
      }}>← Back</button>
      <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 900 }}>Analytics</h1>
      <p style={{ color: C.muted, marginTop: 0, fontSize: 14 }}>{data?.event?.title || "Event performance"} · {data?.range?.label || "All time"}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {RANGES.map((item) => (
          <button key={item.id} type="button" onClick={() => setRange(item.id)} style={chip(range === item.id)}>
            {item.label}
          </button>
        ))}
      </div>
      {range === "custom" ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          <label style={{ color: C.muted, fontSize: 13 }}>
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ display: "block", marginTop: 4, fontSize: 16, maxWidth: "100%" }} />
          </label>
          <label style={{ color: C.muted, fontSize: 13 }}>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ display: "block", marginTop: 4, fontSize: 16, maxWidth: "100%" }} />
          </label>
        </div>
      ) : null}
      {loading ? <p style={{ color: C.muted }}>Loading analytics…</p> : null}
      {error ? <p style={{ color: C.danger }}>{error}</p> : null}
      {summary ? (
        <>
          <div className="pp-analytics-grid" style={{ marginBottom: 14 }}>
            {cards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <section aria-label="Revenue reconciliation" style={{
            marginBottom: 14,
            padding: 14,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            background: C.card,
            minWidth: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <h2 style={{ fontSize: 16, margin: 0 }}>Revenue reconciliation</h2>
              <InfoTip
                label="Revenue reconciliation"
                text="Gross sales minus refunds on completed sales. Fees and your wallet balance are separate."
              />
            </div>
            <p style={{ margin: "10px 0 0", fontWeight: 800, fontSize: "clamp(15px, 4vw, 18px)", lineHeight: 1.35, overflowWrap: "anywhere" }}>
              {formatNairaFromKobo(reconciliation?.grossKobo || 0)}
              {" − "}
              {formatNairaFromKobo(reconciliation?.refundsOnPaidOrdersKobo || 0)}
              {" = "}
              {formatNairaFromKobo(reconciliation?.netKobo || 0)}
            </p>
            <p style={{ color: C.muted, fontSize: 13, margin: "6px 0 0" }}>Gross sales − Refunds = Net revenue</p>
            <button
              type="button"
              aria-expanded={moneyOpen}
              onClick={() => setMoneyOpen((value) => !value)}
              style={{
                marginTop: 10,
                border: `1px solid ${C.border}`,
                background: "transparent",
                color: C.text,
                borderRadius: 999,
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {moneyOpen ? "Hide details" : "Show details"}
            </button>
            {moneyOpen ? (
              <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 14, lineHeight: 1.4 }}>
                <div>Surplus captures: {formatNairaFromKobo(reconciliation?.surplusCapturesKobo || 0)}</div>
                <div>Other refunds on sales: {formatNairaFromKobo(reconciliation?.otherRefundsOnPaidOrdersKobo || 0)}</div>
                <div>Refunds outside sales: {formatNairaFromKobo(reconciliation?.refundsOutsideGrossKobo || 0)}</div>
                <div>Platform fees, excluded: {formatNairaFromKobo(reconciliation?.platformFeesKobo || 0)}</div>
                <div title="Ticket face value still inside the holding period. This is not a failed payment.">Pending settlement: {formatNairaFromKobo(summary?.settlement?.pendingKobo || 0)}</div>
                <div title="Ticket face value released to the organiser.">Available settlement: {formatNairaFromKobo(summary?.settlement?.availableKobo || 0)}</div>
                <div title="Blocked for a refund, cancellation, or another hold.">Withheld: {formatNairaFromKobo(summary?.settlement?.withheldKobo || 0)}</div>
                <div title="Payment processing charged to the buyer. Not organiser ticket revenue.">Payment processing fees: {formatNairaFromKobo(summary?.settlement?.processingFeeKobo || 0)}</div>
                <div>Checkout not finished: {displayCount(summary.checkoutAbandonment)} · {displayPercent(summary.checkoutAbandonment.rate, "tracked")}</div>
              </div>
            ) : null}
          </section>

          <TrendChart
            series={data.series}
            enabled={enabled}
            selected={selected}
            onSelect={setSelected}
            onToggle={(key) => setEnabled((current) => ({ ...current, [key]: !current[key] }))}
          />

          <section style={{ marginTop: 16 }}>
            <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Conversion funnel</h2>
            {(data.funnel || []).map((stage) => {
              const width = stage.state === "not_tracked"
                ? 0
                : stage.fromPreviousPercent == null
                  ? (Number(stage.value) > 0 ? 100 : 0)
                  : Math.max(0, Math.min(100, stage.fromPreviousPercent));
              return (
                <div key={stage.key} style={{
                  padding: "8px 10px",
                  marginBottom: 8,
                  background: C.card,
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  minWidth: 0,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                      <strong style={{ fontSize: 14 }}>{stage.label}</strong>
                      {stage.denominator ? <InfoTip label={stage.label} text={stage.denominator} /> : null}
                    </div>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>
                      {stage.state === "not_tracked" ? "Not tracked" : Number(stage.value || 0).toLocaleString("en-NG")}
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 4, background: C.border, marginTop: 8, overflow: "hidden" }}>
                    <div style={{ width: `${width}%`, height: "100%", background: C.lime, borderRadius: 4 }} />
                  </div>
                  {stage.fromPreviousPercent != null ? (
                    <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>{stage.fromPreviousPercent.toFixed(1)}% of previous</div>
                  ) : null}
                </div>
              );
            })}
          </section>

          <section style={{ marginTop: 16 }}>
            <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Ticket categories</h2>
            {(data.tickets?.rows || []).length === 0 ? <p style={{ color: C.muted, fontSize: 14 }}>No ticket categories yet.</p> : null}
            {(data.tickets?.rows || []).map((row) => {
              const reserved = Number(row.reserved) || 0;
              const available = row.available == null ? Math.max(0, Number(row.remaining) || 0) : Number(row.available);
              const freeCategory = Number(row.priceKobo) === 0;
              return (
                <div key={row.id} style={{
                  padding: 12, borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, marginBottom: 8, minWidth: 0,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <strong>{row.name}</strong>
                    <InfoTip
                      label={row.name}
                      text="Capacity is the total you listed. Sold tickets are paid. Reserved tickets are held for a checkout and are not sales. Available tickets can be bought now."
                    />
                  </div>
                  <div className="pp-stock-grid" style={{ marginTop: 8 }}>
                    <StockLine label="Capacity" value={row.capacity} />
                    <StockLine label={freeCategory ? "Registered" : "Sold"} value={row.soldAllTime} />
                    <StockLine label="Reserved" value={reserved} />
                    <StockLine label="Available" value={available} />
                  </div>
                  <div style={{ fontSize: 13, marginTop: 8, color: C.muted }}>
                    {freeCategory
                      ? `This period: ${row.freeInPeriod || 0} registered · ${formatNairaFromKobo(0)}`
                      : `This period: ${row.soldInPeriod} sold · ${formatNairaFromKobo(row.revenueKobo)}`}
                  </div>
                </div>
              );
            })}
          </section>

          <section style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <h2 style={{ fontSize: 18, margin: 0 }}>Check-in</h2>
              <InfoTip label="Check-in" text="These counts follow tickets sold in the selected dates." />
            </div>
            <div className="pp-checkin-grid" style={{ marginTop: 8 }}>
              <MetricCard label="Tickets issued" value={String(data.checkIn?.ticketsIssued ?? "—")} help={CARD_HELP["Tickets issued"]} />
              <MetricCard label="Checked in" value={String(data.checkIn?.checkedIn ?? "—")} help={CARD_HELP["Checked in"]} />
              <MetricCard label="Unused" value={String(data.checkIn?.unused ?? "—")} help={CARD_HELP.Unused} />
              <MetricCard label="Attendance" value={displayPercent(data.checkIn?.attendanceRate, "tracked")} help={CARD_HELP.Attendance} />
            </div>
            <p style={{ color: C.muted, fontSize: 13, margin: "8px 0 0", display: "flex", alignItems: "center", gap: 6 }}>
              Unused now, all time: {data.checkIn?.unusedAllTime ?? 0}
              <InfoTip label="Unused now" text={CARD_HELP["Unused now"]} />
            </p>
          </section>

          <Disclosure title={`Traffic sources${(data.sources || []).length ? ` (${data.sources.length})` : ""}`}>
            {(data.sources || []).length === 0 ? (
              <p style={{ color: C.muted, fontSize: 14 }}>No visitor sources in this period.</p>
            ) : data.sources.map((row) => (
              <div key={row.channel} style={{
                padding: "10px 12px",
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                background: C.card,
                marginBottom: 8,
              }}>
                <strong>{row.label}</strong>
                <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
                  {row.visitorsState === "not_tracked" ? "Visitors not tracked" : `${row.visitors} visitors`}
                  {" · "}
                  {row.paidOrders} paid
                  {" · "}
                  {row.conversion == null ? "—" : `${row.conversion.toFixed(1)}%`}
                </div>
              </div>
            ))}
          </Disclosure>

          <section style={{ marginTop: 16 }}>
            <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Insights</h2>
            {(data.insights || []).length === 0 ? (
              <p style={{ color: C.muted, fontSize: 14 }}>No insights for this period.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, color: C.text, lineHeight: 1.45, fontSize: 15 }}>
                {(data.insights || []).map((line) => <li key={line} style={{ marginBottom: 8 }}>{line}</li>)}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
